// src/pages/AdminGameProducts.tsx

import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  CREDIT_PERIOD_LABELS,
  GAME_STATUS_LABELS,
  fetchGamesCatalog,
  fetchGameStatuses,
  updateGameStatus,
  updateGameCredits,
  createGame,
  type CreditPeriod,
  type GameInfo,
  type GameStatus,
} from '../utils/gameCatalog';

const AdminGameProducts: React.FC = () => {
  const { user, token } = useAuth();
  const [games, setGames] = useState<GameInfo[]>([]);
  const [gameStatuses, setGameStatuses] = useState<Record<string, GameStatus>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [newGameName, setNewGameName] = useState('');
  const [newGameUrl, setNewGameUrl] = useState('');
  const [newGameStatus, setNewGameStatus] = useState<GameStatus>('planned');
  const [newGameCreditCost, setNewGameCreditCost] = useState('0');
  const [newGameCreditPeriod, setNewGameCreditPeriod] = useState<CreditPeriod>('month');
  const [newGameError, setNewGameError] = useState('');

  useEffect(() => {
    const loadData = async () => {
      if (!token) return;

      setLoading(true);
      // Legacy game_products (pass/pack pricing) is no longer surfaced
      // in this admin page — monetization lives entirely on the credits
      // system now. We only need the catalog + status maps.
      const [catalogData, statusesData] = await Promise.all([
        fetchGamesCatalog(token),
        fetchGameStatuses(token),
      ]);

      setGames(catalogData);
      setGameStatuses(statusesData);

      setLoading(false);
    };

    loadData();
  }, [token]);

  const handleCreateGame = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!token) return;
    setNewGameError('');
    setSaving(true);
    try {
      const newGame = await createGame(token, {
        name: newGameName,
        launchUrl: newGameUrl,
        status: newGameStatus,
        creditCost: parseInt(newGameCreditCost, 10) || 0,
        creditPeriod: newGameCreditPeriod,
      });
      if (newGame) {
        setGames([...games, newGame]);
        setGameStatuses({ ...gameStatuses, [newGame.id]: newGameStatus });
        setNewGameName('');
        setNewGameUrl('');
        setNewGameStatus('planned');
        setNewGameCreditCost('0');
        setNewGameCreditPeriod('month');
      }
    } catch (err: any) {
      setNewGameError(err.message || 'Failed to create game');
    } finally {
      setSaving(false);
    }
  };

  // Inline save handler for the Game Status table's Credits column. Optimistic
  // update, revert on failure, same pattern as handleStatusChange.
  const handleGameCreditsChange = async (gameId: string, creditCost: number, creditPeriod: CreditPeriod) => {
    if (!token) return;
    const prev = games;
    setGames(games.map((g) => (g.id === gameId ? { ...g, creditCost, creditPeriod } : g)));
    const updated = await updateGameCredits(token, gameId, { creditCost, creditPeriod });
    if (!updated) {
      setGames(prev);
    }
  };

  const handleStatusChange = async (gameId: string, status: GameStatus) => {
    if (!token) return;

    // Optimistically update the UI
    const prevStatuses = { ...gameStatuses };
    setGameStatuses({ ...gameStatuses, [gameId]: status });

    const success = await updateGameStatus(token, gameId, status);
    if (!success) {
      // Revert on failure
      setGameStatuses(prevStatuses);
    }
  };

  if (loading) {
    return (
      <div className="section container" style={{ textAlign: 'center', padding: '4rem' }}>
        <p>Loading games...</p>
      </div>
    );
  }

  return (
    <div className="section container">
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div>
          <h1 style={{ marginBottom: '0.25rem' }}>Game Management</h1>
          <p className="text-secondary">Signed in as {user?.email}</p>
        </div>

        {/* Add New Game */}
        <div style={{ marginTop: '2rem' }}>
          <h2 style={{ marginBottom: '1rem' }}>Add New Game</h2>
          <div className="card" style={{ padding: '1.5rem' }}>
            <form onSubmit={handleCreateGame} style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 200px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Game Name</label>
                <input
                  type="text"
                  value={newGameName}
                  onChange={(e) => setNewGameName(e.target.value)}
                  required
                  placeholder="e.g. My New Game"
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </div>
              <div style={{ flex: '2 1 280px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Launch URL</label>
                <input
                  type="url"
                  value={newGameUrl}
                  onChange={(e) => setNewGameUrl(e.target.value)}
                  required
                  placeholder="https://game.snowbirdindustries.us"
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </div>
              <div style={{ flex: '0 1 160px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Status</label>
                <select
                  value={newGameStatus}
                  onChange={(e) => setNewGameStatus(e.target.value as GameStatus)}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                >
                  {Object.entries(GAME_STATUS_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: '0 1 110px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Credits</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={newGameCreditCost}
                  onChange={(e) => setNewGameCreditCost(e.target.value)}
                  placeholder="0"
                  title="0 = free"
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                />
              </div>
              <div style={{ flex: '0 1 140px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Period</label>
                <select
                  value={newGameCreditPeriod}
                  onChange={(e) => setNewGameCreditPeriod(e.target.value as CreditPeriod)}
                  style={{ width: '100%', padding: '0.6rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
                >
                  {Object.entries(CREDIT_PERIOD_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <button type="submit" className="btn btn-primary" disabled={saving} style={{ whiteSpace: 'nowrap' }}>
                  {saving ? 'Adding...' : 'Add Game'}
                </button>
              </div>
            </form>
            {newGameError && <p style={{ color: '#dc2626', marginTop: '0.75rem', fontSize: '0.875rem' }}>{newGameError}</p>}
          </div>
        </div>

        {/* Game Status Management */}
        <div style={{ marginTop: '2rem' }}>
          <h2 style={{ marginBottom: '1rem' }}>Game Status</h2>
          <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Game</th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Credits</th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Visibility</th>
                </tr>
              </thead>
              <tbody>
                {games.map((game) => {
                  const status = gameStatuses[game.id] || game.status || 'active';
                  const isVisible = status === 'beta' || status === 'active';
                  return (
                    <tr key={game.id}>
                      <td style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb' }}>
                        <div>
                          <strong>{game.name}</strong>
                          <p className="text-secondary" style={{ fontSize: '0.875rem', margin: '0.25rem 0 0' }}>
                            {game.id}
                          </p>
                        </div>
                      </td>
                      <td style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb' }}>
                        <select
                          value={status}
                          onChange={(e) => handleStatusChange(game.id, e.target.value as GameStatus)}
                          style={{
                            padding: '0.5rem 0.75rem',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db',
                            backgroundColor: 'white',
                            minWidth: '150px',
                          }}
                        >
                          {Object.entries(GAME_STATUS_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb' }}>
                        {/* Inline credit config — saves on blur so admins
                            don't need a modal for a two-field change. */}
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={game.creditCost}
                            onChange={(e) => {
                              const n = parseInt(e.target.value, 10) || 0;
                              setGames(games.map((g) => (g.id === game.id ? { ...g, creditCost: n } : g)));
                            }}
                            onBlur={() => handleGameCreditsChange(game.id, game.creditCost, game.creditPeriod)}
                            style={{
                              width: '70px',
                              padding: '0.4rem 0.5rem',
                              borderRadius: '6px',
                              border: '1px solid #d1d5db',
                              textAlign: 'right',
                            }}
                            title="0 = free"
                          />
                          <select
                            value={game.creditPeriod}
                            onChange={(e) => {
                              const p = e.target.value as CreditPeriod;
                              setGames(games.map((g) => (g.id === game.id ? { ...g, creditPeriod: p } : g)));
                              handleGameCreditsChange(game.id, game.creditCost, p);
                            }}
                            style={{
                              padding: '0.4rem 0.5rem',
                              borderRadius: '6px',
                              border: '1px solid #d1d5db',
                              backgroundColor: 'white',
                            }}
                          >
                            {Object.entries(CREDIT_PERIOD_LABELS).map(([value, label]) => (
                              <option key={value} value={value}>{label}</option>
                            ))}
                          </select>
                        </div>
                      </td>
                      <td style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '9999px',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            backgroundColor: isVisible ? '#dcfce7' : '#f3f4f6',
                            color: isVisible ? '#166534' : '#6b7280',
                          }}
                        >
                          {isVisible ? 'Visible to Users' : 'Admin Only'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-secondary" style={{ fontSize: '0.875rem', marginTop: '0.75rem' }}>
            Only games with Beta or Active status are visible to non-admin users.
          </p>
        </div>

      </div>
    </div>
  );
};

export default AdminGameProducts;
